import { JWMButton } from "@components/JWMButton/JWMButton";
import { JWMHtml } from "@components/JWMHtml/JWMHtml";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Card, ImagePosition } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import { CarouselSlide } from "@jmc/solid-design-system/src/components/organisms/ImageCarousel/CarouselSlide/CarouselSlide";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery/useMediaQuery";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { CMSColor } from "@jwm/types/CMSColor";
import { CMSLinkGroupProps } from "@jwm/types/CMSLinkGroupProps";
import { default as cmsMapper } from "@utils/cms-mapper";
import { getVisitedLinkColor } from "@utils/colorsUtil";
import classnames from "classnames";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import React, { useEffect, useState } from "react";

import style from "../style.module.scss";
export type Variants = "contained" | "outlined" | "icon";
export type Colors = "primary" | "secondary" | "accent" | "success" | "warning" | "error" | "grey" | "transparent";

interface Props {
    slide: {
        button?: {
            text: string;
            link_group: CMSLinkGroupProps;
            service_link?: { service_page: string; specialty: { title: string }[] };
        };
        display_options?: {
            view_mode?: string;
            align_content?: string;
            align_content_vertically?: string;
            background_color?: CMSColor;
            text_color?: CMSColor;
            title_color?: CMSColor;
            sub_title_reference_and_link_color?: CMSColor;
            image_focus?: ImagePosition;
        };
        carousel_text: string;
        title: string;
        image?: { description: string; url: string; gatsbyImageData: IGatsbyImageData };
    };
    buttonProps?: {
        variant: Variants;
        color: Colors;
    };
    showInHeader: boolean;
    height: string;
    placement?: string;
    titleComponent?: React.ElementType;
    loading?: "lazy" | "eager";
    siteSpecialty?: {
        type: string;
        title: string;
    };
    headerVariant?: boolean;
}

export const SplitImage = (props: Props): JSX.Element => {
    const {
        slide,
        showInHeader,
        height,
        buttonProps,
        placement,
        titleComponent,
        loading = "lazy",
        siteSpecialty,
        headerVariant = false,
    } = props;

    const { jnjFullBranded } = useJnjBranding();
    const isMobile = useMediaQuery(BreakPoint.md);
    const viewMode = jnjFullBranded
        ? slide?.display_options?.view_mode === "Image-Text"
            ? "Image-Text"
            : "Text-Image"
        : slide?.display_options?.view_mode;
    const alignHorizontal = jnjFullBranded
        ? "start"
        : slide.display_options.align_content
        ? slide.display_options.align_content?.toLowerCase()
        : "center";
    const alignVertical = jnjFullBranded
        ? "center"
        : slide.display_options.align_content_vertically
        ? slide.display_options.align_content_vertically?.toLowerCase()
        : "center";

    const imageFocus = slide?.display_options?.image_focus?.replace("-", " ") || "center center";

    const titleColor =
        (!jnjFullBranded && cmsMapper.mapCarouselColors(slide?.display_options?.title_color)) || "--text-dark";
    const textColor =
        (!jnjFullBranded && cmsMapper.mapCarouselColors(slide?.display_options?.text_color)) || "--text-light";
    const backgroundColor =
        (jnjFullBranded ? "--transparent" : cmsMapper.mapCarouselColors(slide?.display_options?.background_color)) ||
        "--white";
    const linkColor =
        cmsMapper.mapCarouselColors(slide?.display_options?.sub_title_reference_and_link_color) || "--text-link";
    const [visitedLinkColor, setVisitedLinkColor] = useState(undefined);

    useEffect(() => {
        setVisitedLinkColor(getVisitedLinkColor(linkColor));
    }, []);

    const slideButton = {
        text: slide?.button?.text,
        anchor_id: slide?.button?.link_group?.anchor_id,
        external: slide?.button?.link_group?.internal_page?.length === 0,
        url:
            slide?.button?.link_group?.internal_page?.length > 0
                ? slide?.button?.link_group?.internal_page?.[0]?.url
                : slide?.button?.link_group?.external_link,
        url_prefix: slide?.button?.link_group?.internal_page?.[0]?.site_reference?.[0]?.url_prefix,
        props: {
            variant: buttonProps?.variant,
            color: buttonProps?.color || "primary",
        },
        serviceLink: {
            servicePage: slide?.button?.service_link?.service_page,
            specialty: { title: slide?.button?.service_link?.specialty?.[0]?.title },
        },
    };

    const hasButtonProps = (): boolean => {
        if (slideButton.url || slideButton?.serviceLink?.servicePage) {
            return true;
        }
        return false;
    };

    const accessoryRight = jnjFullBranded ? <Card.CTAIconArrow /> : null;

    const button = (): JSX.Element => {
        if (hasButtonProps()) {
            return (
                <JWMButton
                    buttonProps={
                        jnjFullBranded
                            ? headerVariant
                                ? {
                                      color: "primary",
                                      variant: "contained",
                                  }
                                : {
                                      color: "primary",
                                      flexibleWidth: true,
                                      justifiedBetween: true,
                                      variant: "naked",
                                      noSpacing: true,
                                  }
                            : slideButton?.props
                    }
                    text={slideButton?.text}
                    fullWidth={jnjFullBranded && headerVariant && isMobile}
                    anchor_id={slideButton?.anchor_id}
                    url={slideButton?.url}
                    url_prefix={slideButton?.url_prefix}
                    external={slideButton?.external}
                    serviceLink={slideButton?.serviceLink}
                    accessoryRight={!(jnjFullBranded && headerVariant) && accessoryRight}
                />
            );
        }
    };

    const slideText = (
        <>
            <div
                className={classnames(
                    style[`carousel__slide__text_title`],
                    style[`carousel__slide__${viewMode}__text_title`],
                )}
            >
                <span
                    style={
                        {
                            "--text-primary": `var(${titleColor})`,
                            "--text-link": `var(${linkColor})`,
                            "--text-link-visited": visitedLinkColor,
                        } as React.CSSProperties
                    }
                >
                    <Typography
                        variant={showInHeader ? (jnjFullBranded && headerVariant ? "h1" : "h4") : "h3"}
                        color={"primary"}
                        component={titleComponent}
                    >
                        <JWMHtml color="inherit" showGlossary={false}>
                            {slide.title}
                        </JWMHtml>
                    </Typography>
                </span>
            </div>
            <div className={classnames(style[`carousel__slide__text_description`])}>
                <span
                    style={
                        {
                            "--text-primary": `var(${textColor})`,
                            "--text-link": `var(${linkColor})`,
                            "--text-link-visited": visitedLinkColor,
                        } as React.CSSProperties
                    }
                >
                    <Typography
                        color={jnjFullBranded ? (headerVariant ? "dark" : "light") : "primary"}
                        variant={jnjFullBranded ? (headerVariant ? "body-01" : "body-03") : "body"}
                    >
                        <JWMHtml color="inherit" placement={placement} siteSpecialty={siteSpecialty}>
                            {slide?.carousel_text}
                        </JWMHtml>
                    </Typography>
                </span>
            </div>
            {hasButtonProps() || !jnjFullBranded ? (
                <div className={classnames(style[`carousel__slide__button`])}>{button()}</div>
            ) : null}
        </>
    );

    const slideImage = (
        <GatsbyImage
            alt={slide?.title || ""}
            image={slide?.image?.gatsbyImageData}
            style={
                jnjFullBranded
                    ? {
                          top: 0,
                          insetInlineStart: 0,
                          paddingBlockStart: "75%",
                          height: 0,
                      }
                    : {
                          position: "absolute",
                          width: "100%",
                          height: "100%",
                          top: 0,
                          insetInlineStart: 0,
                      }
            }
            imgStyle={{ objectPosition: imageFocus }}
            loading={loading}
        />
    );

    if (jnjFullBranded) {
        return (
            <CarouselSlide
                title={slide?.title}
                image={slide?.image?.gatsbyImageData && slideImage}
                viewMode={viewMode}
                headerVariant={headerVariant}
            >
                {slideText}
            </CarouselSlide>
        );
    }

    return (
        <div
            style={{ minHeight: height, height: "100%" }}
            className={classnames(style.carousel__slide, style[`carousel__slide__${viewMode}`])}
        >
            <div
                className={classnames(
                    style[`carousel__slide__text_wrapper`],
                    style[`carousel__slide__text_wrapper__background${backgroundColor}`],
                    style[`carousel__slide__text_wrapper_align_horizontal_${alignHorizontal}`],
                    style[`carousel__slide__text_wrapper_align_vertical_${alignVertical}`],
                    hasButtonProps() ? style[`carousel__slide__text_wrapper_carousel_controls`] : "",
                )}
            >
                {slideText}
            </div>
            <div
                className={classnames(
                    style[`carousel__slide__backgroundImage_wrapper`],
                    style[`carousel__slide__backgroundImage_wrapper_${height}`],
                    !slide?.image
                        ? style[`carousel__slide__backgroundImage_wrapper__background${backgroundColor}`]
                        : "",
                )}
            >
                {slideImage}
            </div>
        </div>
    );
};
